import React from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SortIcon } from '@/components/shared/reports/SortIcon';
import { ReportPagination } from '@/components/shared/reports/ReportPagination';
import { FileText, Eye, Loader2 } from 'lucide-react';
import type { AuditTrailRow, SortKey, SortDir } from '@/utils/AuditTrail/types';
import { StatusBadge } from './StatusBadge';

interface ResultsTableProps {
  data: AuditTrailRow[];
  totalCount: number;
  isLoading: boolean;
  onRowClick: (row: AuditTrailRow) => void;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  page: number;
  onPageChange: (p: number) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  columnVisibility: Record<string, boolean>;
}

export const ResultsTable: React.FC<ResultsTableProps> = ({
  data,
  totalCount,
  isLoading,
  onRowClick,
  sortKey,
  sortDir,
  onSort,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  columnVisibility,
}) => {
  const col = (id: string) => columnVisibility[id] !== false;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const SortHeader: React.FC<{ col: SortKey; label: string }> = ({ col, label }) => (
    <TableHead
      className="cursor-pointer select-none whitespace-nowrap"
      onClick={() => onSort(col)}
    >
      {label}
      <SortIcon col={col} current={sortKey} dir={sortDir} />
    </TableHead>
  );

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
        {isLoading ? (
          <Loader2 className="h-10 w-10 opacity-30 animate-spin" />
        ) : (
          <>
            <FileText className="h-10 w-10 opacity-30" />
            <p className="text-sm">No timesheets match your filters.</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {col('employeeName') && <SortHeader col="employeeName" label="Employee Name" />}
                {col('weekEnding') && <SortHeader col="weekEnding" label="Week Ending" />}
                {col('department') && <TableHead>Department</TableHead>}
                {col('status') && <TableHead>Status</TableHead>}
                {col('lastEvent') && <TableHead>Last Event</TableHead>}
                {col('totalHours') && <SortHeader col="totalHours" label="Total Hours" />}
                <TableHead className="w-16">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => onRowClick(row)}
                >
                  {col('employeeName') && <TableCell className="font-medium">{row.employeeName}</TableCell>}
                  {col('weekEnding') && <TableCell className="text-sm">{format(row.weekEnding, 'dd/MM/yyyy')}</TableCell>}
                  {col('department') && <TableCell className="text-sm text-muted-foreground">{row.department}</TableCell>}
                  {col('status') && <TableCell><StatusBadge status={row.status} /></TableCell>}
                  {col('lastEvent') && (
                    <TableCell className="text-sm text-muted-foreground max-w-[260px] truncate" title={row.lastEvent}>
                      {row.lastEvent}
                    </TableCell>
                  )}
                  {col('totalHours') && <TableCell className="text-sm font-medium">{row.totalHours}h</TableCell>}
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(row);
                      }}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <ReportPagination
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <p className="text-xs text-muted-foreground text-right">
        Showing {Math.min((page - 1) * pageSize + 1, totalCount)}–{Math.min((page - 1) * pageSize + data.length, totalCount)} of {totalCount} timesheets
      </p>
    </div>
  );
};
